/**
 * useJobsSearch - Search jobs across 3 parallel query paths
 *
 * Extracted from useJobs.ts (lines 188-235) for focused search logic
 * Searches by name, DNS, and source in parallel and merges results
 */

import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { JobFilters } from '@/sections/jobs/types';
import { mergeJobArrays } from '@/sections/jobs/utils/jobCollections.util';
import { Job, ResourceType } from '@/types';

/**
 * Hook for searching jobs across 3 parallel query paths
 *
 * Queries:
 * 1. Search by job name (name:search)
 * 2. Search by DNS/target (#job#search)
 * 3. Search by source capability (source:search)
 *
 * Results are merged and deduplicated by job key.
 * Uses 0ms debounce when clearing (prevents empty table bug),
 * 300ms debounce when typing.
 *
 * @param filter - Job filter configuration with search term
 * @param enabled - Whether search queries should be enabled
 * @returns Merged search results with unified interface
 */
export function useJobsSearch(filter: JobFilters, enabled: boolean) {
  // Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const [debouncedSearch] = useDebounce(
    filter.search,
    filter.search === '' ? 0 : 300
  );

  // Query 1: Search by job name
  const {
    data: jobSearchByName,
    refetch: refreshJobSearchByName,
    status: jobSearchByNameStatus,
    error: jobSearchByNameError,
    isFetching: isFetchingjobSearchByName,
    fetchNextPage: jobSearchByNameFetchNextPage,
    isFetchingNextPage: jobSearchByNameIsFetchingNextPage,
    hasNextPage: jobSearchByNameHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `name:${debouncedSearch}`,
    },
    { enabled, refetchInterval: 5000, placeholderData: keepPreviousData }
  );

  // Query 2: Search by DNS/target
  const {
    data: jobSearchByDns,
    refetch: refreshJobSearchByDns,
    status: jobSearchByDnsStatus,
    error: jobSearchByDnsError,
    isFetching: isFetchingjobSearchByDns,
    fetchNextPage: jobSearchByDnsFetchNextPage,
    isFetchingNextPage: jobSearchByDnsIsFetchingNextPage,
    hasNextPage: jobSearchByDnsHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `#job#${debouncedSearch}`,
    },
    { enabled, refetchInterval: 5000, placeholderData: keepPreviousData }
  );

  // Query 3: Search by source
  const {
    data: jobSearchBySource,
    refetch: refreshJobSearchBySource,
    status: jobSearchBySourceStatus,
    error: jobSearchBySourceError,
    isFetching: isFetchingjobSearchBySource,
    fetchNextPage: jobSearchBySourceFetchNextPage,
    isFetchingNextPage: jobSearchBySourceIsFetchingNextPage,
    hasNextPage: jobSearchBySourceHasNextPage,
  } = useGenericSearch(
    {
      label: ResourceType.Job,
      query: `source:${debouncedSearch}`,
    },
    { enabled, refetchInterval: 5000, placeholderData: keepPreviousData }
  );

  // Merge results from all 3 queries, deduplicating by job key
  // Uses extracted utility function for cleaner merge logic
  const mergedJobs: Job[] = useMemo(() => {
    return mergeJobArrays(
      jobSearchByName?.jobs,
      jobSearchByDns?.jobs,
      jobSearchBySource?.jobs
    );
  }, [
    jobSearchByName?.jobs?.length ?? 0,
    jobSearchByDns?.jobs?.length ?? 0,
    jobSearchBySource?.jobs?.length ?? 0,
  ]);

  // Unified refetch function for all 3 queries
  const refetch = () => {
    refreshJobSearchByName();
    refreshJobSearchByDns();
    refreshJobSearchBySource();
  };

  // Unified fetchNextPage for all 3 queries
  const fetchNextPage = () => {
    jobSearchByNameFetchNextPage();
    jobSearchByDnsFetchNextPage();
    jobSearchBySourceFetchNextPage();
  };

  // Aggregate errors (return first error encountered)
  const error =
    jobSearchByNameError || jobSearchByDnsError || jobSearchBySourceError;

  // Aggregate fetching states
  const isFetching =
    isFetchingjobSearchByName ||
    isFetchingjobSearchByDns ||
    isFetchingjobSearchBySource;

  const isFetchingNextPage =
    jobSearchByNameIsFetchingNextPage ||
    jobSearchByDnsIsFetchingNextPage ||
    jobSearchBySourceIsFetchingNextPage;

  const hasNextPage =
    jobSearchByNameHasNextPage ||
    jobSearchByDnsHasNextPage ||
    jobSearchBySourceHasNextPage;

  return {
    data: mergedJobs,
    refetch,
    error,
    status: [
      jobSearchByNameStatus,
      jobSearchByDnsStatus,
      jobSearchBySourceStatus,
    ],
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    debouncedSearch,
  };
}
